/**
 * Planner-specific Vue Router guards.
 *
 * Phase 7 moves the `/:guildId/plan/:raidId` snapshot-selection cascade from the
 * host router into this module. The planner install entrypoint
 * registers the guard via `router.beforeEach(...)` with an async
 * handler, keeping the same behavior the planner shipped before this
 * refactor:
 *
 *   - Calendar, Guild settings, and Audit log URLs resolve their guild parameter against
 *     the user's available guilds before that guild becomes active.
 *   - Only navigations to the guild-scoped planner route trigger the
 *     raid-selection cascade.
 *   - The previous snapshot is cleared first via `selectRaid(null)` so
 *     a bad, deleted, or superseded URL cannot leave stale data
 *     selected while the user is redirected back to Calendar.
 *   - A non-numeric raid id, or anything that doesn't match the
 *     positive-snowflake regex, returns the user to Calendar.
 *   - `selectRaid(raidId)` resolves to `false`, or the resolved raid
 *     doesn't match the URL by the time the guard inspects the store,
 *     also returns to Calendar and clears the partial snapshot.
 *
 * This stays in a `beforeEach` rather than the route record's
 * `beforeEnter` because the parameterized planner route fires
 * `beforeEnter` only on record entry — `beforeEach` fires on every
 * navigation including param-only updates. The regression test under
 * `apps/frontend/src/router/__tests__/plannerRaidNavigation.test.ts`
 * pins this contract.
 */
import type { Router } from 'vue-router'

import { useGvgPlannerStore } from './stores/gvgPlannerStore'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { useGuild } from '@/shared/ports/guild'
import { FEATURE_ROUTE_NAMES } from './constants/routes'

function isValidGuildId(value: string): boolean {
  return /^[1-9]\d{16,19}$/.test(value)
}

function activeGuildCalendarLocation(guildId: string):
  | { readonly name: typeof APP_ROUTE_NAMES.Calendar; readonly params: { readonly guildId: string } }
  | { readonly name: typeof FEATURE_ROUTE_NAMES.Onboarding } {
  const guild = useGuild()
  const requested = guild.availableGuilds.value.find(
    (candidate) => candidate.discordGuildId === guildId,
  )
  const active = guild.availableGuilds.value.find(
    (candidate) => candidate.discordGuildId === guild.activeDiscordGuildId.value,
  )
  const fallback = requested ?? active ?? guild.availableGuilds.value[0]
  return fallback === undefined
    ? { name: FEATURE_ROUTE_NAMES.Onboarding }
    : {
        name: APP_ROUTE_NAMES.Calendar,
        params: { guildId: fallback.discordGuildId },
      }
}



/**
 * Register the planner's navigation guard on the host router. Idempotent
 * — the guard body reads `to.name` on each navigation so re-registration
 * would just stack identical guards; `installGvgPlanner()` only runs
 * once per app boot so this concern is moot.
 */
export function registerPlannerRouteGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const isGuildRoute = to.name === APP_ROUTE_NAMES.Calendar
      || to.name === FEATURE_ROUTE_NAMES.Officers
      || to.name === FEATURE_ROUTE_NAMES.AuditLog
      || to.name === FEATURE_ROUTE_NAMES.GvgPlanner
    if (isGuildRoute) {
      const guildId = to.params['guildId']
      if (typeof guildId !== 'string' || !isValidGuildId(guildId)) {
        return activeGuildCalendarLocation(useGuild().activeDiscordGuildId.value)
      }

      const guild = useGuild()
      // `availableGuilds` is the `canView` projection. Refresh it for every
      // guild-scoped navigation instead of trusting a prior selector result:
      // a Discord role can be revoked between routes, and a locked membership
      // must never reach `setActive()` or the planner fetch cascade.
      await guild.loadAvailableGuilds()
      if (!guild.availableGuilds.value.some((candidate) => candidate.discordGuildId === guildId)) {
        return activeGuildCalendarLocation(guild.activeDiscordGuildId.value)
      }
      if (guild.activeDiscordGuildId.value !== guildId) {
        await guild.setActive(guildId)
      }
    }

    if (to.name !== FEATURE_ROUTE_NAMES.GvgPlanner) {
      return true
    }

    const store = useGvgPlannerStore()

    const raidId = to.params['raidId']
    const isValidRaidId = typeof raidId === 'string' && /^[1-9]\d*$/.test(raidId)

    // Short-circuit when the URL already names the currently-loaded raid.
    // Without this, clicking the "Raid plan" tab while a raid is selected
    // would: clear the snapshot synchronously (tab disappears), then
    // reload the same raid (tab reappears) — a visible flicker. The
    // guard's clear-and-load sequence is still needed for a different
    // raid id so a bad, deleted, or superseded URL cannot leave stale
    // data selected while the user is redirected back to the calendar.
    if (
      isValidRaidId
      && raidId === store.activeRaidId
      && store.activeRaidBoard?.raid.id === raidId
    ) {
      return true
    }

    // Different (or missing) raid — `selectRaid` restores its prior
    // snapshot when loading fails. Clear it first so a bad, deleted, or
    // superseded URL cannot leave stale data selected while the user is
    // redirected back to the calendar.
    await store.selectRaid(null)

    if (!isValidRaidId) {
      return activeGuildCalendarLocation(store.activeDiscordGuildId)
    }

    const selected = await store.selectRaid(raidId)
    if (!selected || store.activeRaidBoard?.raid.id !== raidId) {
      await store.selectRaid(null)
      return activeGuildCalendarLocation(store.activeDiscordGuildId)
    }

    return true
  })
}
